import React, { useState, useEffect } from "react";
import * as XLSX from 'xlsx';
import "./TradeReport.css";

const TradeReport = () => {
  const [tradeData, setTradeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterSymbol, setFilterSymbol] = useState("All");
  const [filterType, setFilterType] = useState("All");

  const API_URL = "https://server.account.majestyfx.com/admin_tradereport.php";

  useEffect(() => {
    fetchTradeData();
  }, []);

  const fetchTradeData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(API_URL, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setTradeData(data);
      } else {
        throw new Error('Failed to fetch trade data');
      }
    } catch (err) {
      console.error('Error fetching trade data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getUniqueSymbols = () => {
    if (!tradeData?.trades) return [];
    return [...new Set(tradeData.trades.map(trade => trade.symbol))];
  };

  const getUniqueActions = () => {
    if (!tradeData?.trades) return [];
    return [...new Set(tradeData.trades.map(trade => trade.action))];
  };

  const filteredTrades = tradeData?.trades?.filter(trade => {
    const matchesSearch = trade.login.toString().includes(searchTerm) || 
                         trade.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         trade.comment.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSymbol = filterSymbol === "All" || trade.symbol === filterSymbol;
    const matchesType = filterType === "All" || trade.action === filterType;
    
    return matchesSearch && matchesSymbol && matchesType;
  }) || [];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatNumber = (value, decimals = 2) => {
    return Number(value).toFixed(decimals);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const exportToExcel = () => {
    if (filteredTrades.length === 0) {
      alert('No data to export');
      return;
    }

    const exportData = filteredTrades.map((trade, idx) => ({
      '#': idx + 1,
      'Login': trade.login,
      'Action': trade.action,
      'Symbol': trade.symbol,
      'Volume': formatNumber(trade.volume, 2),
      'Open Price': formatNumber(trade.open_price, 5),
      'Current Price': formatNumber(trade.current_price, 5),
      'Pips': formatNumber(trade.pips, 2),
      'Swap': formatNumber(trade.swap, 2),
      'Profit/Loss': formatNumber(trade.profit_loss, 2),
      'Open Time': formatDate(trade.open_time),
      'Comment': trade.comment
    }));

    // Add summary row
    exportData.push({
      '#': '',
      'Login': '',
      'Action': '',
      'Symbol': 'TOTALS',
      'Volume': tradeData?.totals ? formatNumber(tradeData.totals.total_volume, 2) : '0.00',
      'Open Price': '',
      'Current Price': '',
      'Pips': tradeData?.totals ? formatNumber(tradeData.totals.total_pips, 2) : '0.00',
      'Swap': tradeData?.totals ? formatNumber(tradeData.totals.total_swaps, 2) : '0.00',
      'Profit/Loss': tradeData?.totals ? formatNumber(tradeData.totals.total_pl, 2) : '0.00',
      'Open Time': '',
      'Comment': ''
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Trade Report');
    XLSX.writeFile(wb, `Trade_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const renderTradeTable = () => {
    return (
      <div className="tradereport-table-container">
        <table className="tradereport-table">
          <thead>
            <tr>
              <th>Login</th>
              <th>Action</th>
              <th>Symbol</th>
              <th>Volume</th>
              <th>Open Price</th>
              <th>Current Price</th>
              <th>Pips</th>
              <th>Swap</th>
              <th>Profit/Loss</th>
              <th>SL</th>
              <th>TP</th>
              <th>Created</th>
              <th>Updated</th>
              <th>Comment</th>
            </tr>
          </thead>
          <tbody>
            {filteredTrades.length > 0 ? (
              filteredTrades.map((trade, index) => {
                const isProfit = trade.profit >= 0;
                return (
                  <tr key={index} className="tradereport-table-row">
                    <td className="tradereport-login-cell">{trade.login}</td>
                    <td>
                      <span className={`tradereport-action-badge ${trade.action.toLowerCase()}`}>
                        {trade.action}
                      </span>
                    </td>
                    <td className="tradereport-symbol-cell">{trade.symbol}</td>
                    <td className="tradereport-number-cell">{formatNumber(trade.volume, 2)}</td>
                    <td className="tradereport-price-cell">{formatNumber(trade.price_open, 5)}</td>
                    <td className="tradereport-price-cell">{formatNumber(trade.price_current, 5)}</td>
                    <td className={`tradereport-pips-cell ${trade.pips >= 0 ? 'positive' : 'negative'}`}>
                      {formatNumber(trade.pips, 2)}
                    </td>
                    <td className="tradereport-currency-cell">{formatCurrency(trade.swap)}</td>
                    <td className={`tradereport-profit-cell ${isProfit ? 'positive' : 'negative'}`}>
                      {formatCurrency(trade.profit)}
                    </td>
                    <td className="tradereport-level-cell">
                      {trade.sl > 0 ? formatNumber(trade.sl, 5) : '-'}
                    </td>
                    <td className="tradereport-level-cell">
                      {trade.tp > 0 ? formatNumber(trade.tp, 5) : '-'}
                    </td>
                    <td className="tradereport-date-cell">{formatDate(trade.time_create)}</td>
                    <td className="tradereport-date-cell">{formatDate(trade.time_update)}</td>
                    <td className="tradereport-comment-cell">
                      {trade.comment || '-'}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="14" className="tradereport-no-data-row">
                  <div className="tradereport-no-data-content">
                    <div className="tradereport-no-data-icon">📊</div>
                    <h3>No trade data found</h3>
                    <p>
                      {searchTerm || filterSymbol !== "All" || filterType !== "All" 
                        ? "No trades match your current filters. Try adjusting your search criteria."
                        : "No trade data available at the moment."
                      }
                    </p>
                    {(searchTerm || filterSymbol !== "All" || filterType !== "All") && (
                      <button 
                        onClick={() => {
                          setSearchTerm("");
                          setFilterSymbol("All");
                          setFilterType("All");
                        }}
                        className="tradereport-clear-filters-btn"
                      >
                        Clear Filters
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="tradereport-container">
      <main className="tradereport-main-content">
        
        {/* Header Bar */}
        <div className="tradereport-header-bar">
          <h1 className="tradereport-page-title">Trade Report</h1>
          <div className="tradereport-breadcrumb">
            <a href="/">Home</a> / <span>Trade Report</span>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="tradereport-stats-container">
          <div className="tradereport-stat-box">
            <p>Total P/L</p>
            <h3 className={tradeData?.totals?.total_pl >= 0 ? 'positive' : 'negative'}>
              {tradeData?.totals ? formatCurrency(tradeData.totals.total_pl) : '$0.00'}
            </h3>
          </div>
          <div className="tradereport-stat-box">
            <p>Total Pips</p>
            <h3 className={tradeData?.totals?.total_pips >= 0 ? 'positive' : 'negative'}>
              {tradeData?.totals ? formatNumber(tradeData.totals.total_pips, 2) : '0.00'}
            </h3>
          </div>
          <div className="tradereport-stat-box">
            <p>Total Volume</p>
            <h3>{tradeData?.totals ? formatNumber(tradeData.totals.total_volume, 2) : '0.00'}</h3>
          </div>
          <div className="tradereport-stat-box">
            <p>Total Swaps</p>
            <h3>{tradeData?.totals ? formatCurrency(tradeData.totals.total_swaps) : '$0.00'}</h3>
          </div>
          <div className="tradereport-stat-box">
            <p>Total Trades</p>
            <h3>{tradeData?.total_trades || 0}</h3>
          </div>
        </div>

        {/* Filters and Controls */}
        <div className="tradereport-controls">
          <div className="tradereport-search-section">
            <input
              type="text"
              placeholder="Search by login, symbol, or comment..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="tradereport-search-input"
            />
          </div>
          
          <div className="tradereport-filters">
            <div className="tradereport-filter-group">
              <label>Symbol</label>
              <select 
                value={filterSymbol} 
                onChange={(e) => setFilterSymbol(e.target.value)}
                className="tradereport-filter-select"
              >
                <option value="All">All Symbols</option>
                {getUniqueSymbols().map(symbol => (
                  <option key={symbol} value={symbol}>{symbol}</option>
                ))}
              </select>
            </div>
            <div className="tradereport-filter-group">
              <label>Type</label>
              <select 
                value={filterType} 
                onChange={(e) => setFilterType(e.target.value)}
                className="tradereport-filter-select"
              >
                <option value="All">All Types</option>
                {getUniqueActions().map(action => (
                  <option key={action} value={action}>{action}</option>
                ))}
              </select>
            </div>
            <div className="tradereport-filter-group">
              <button 
                onClick={fetchTradeData} 
                className="tradereport-refresh-btn"
                disabled={loading}
              >
                {loading ? 'Refreshing...' : 'Refresh Data'}
              </button>
              <button 
                onClick={exportToExcel} 
                className="tradereport-excel-btn"
                disabled={loading || filteredTrades.length === 0}
                style={{ marginLeft: '10px', backgroundColor: '#28a745', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '500' }}
              >
                Export to Excel
              </button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="tradereport-error-message">
            <div className="tradereport-error-icon">⚠️</div>
            <div className="tradereport-error-text">
              <strong>Error loading trade data:</strong> {error}
            </div>
            <button 
              onClick={fetchTradeData} 
              className="tradereport-retry-btn"
            >
              Retry
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="tradereport-loading">
            <div className="tradereport-loading-spinner"></div>
            <p>Loading trade data...</p>
          </div>
        )}

        {/* Trade Cards Container */}
        {!loading && !error && (
          <div className="tradereport-content">
          <div className="tradereport-results-info">
            <span>Showing {filteredTrades.length} of {tradeData?.total_trades || 0} trades</span>
          </div>
          
          {renderTradeTable()}
        </div>
        )}

        {/* Footer */}
        <div className="tradereport-footer">
          <p>
            Copyright © 2025 <span>Ample Markets</span>. All rights reserved.
          </p>
          <p className="tradereport-version">Version 2.0</p>
        </div>
      </main>
    </div>
  );
};

export default TradeReport;

